// OTROS INGRESOS DE UNA MISMA PERSONA.
//
// Idea del apartado de ideas, aprobada por Lissette el 2026-10-27: *"En
// ocasiones un solo cliente tiene dos tipos de ingresos diferentes, ya sea por
// cuenta ajena o dos trabajos. No es posible dividir los ingresos de esa
// persona a menos que los ingresos vengan del esposo, hijos, etc."*
//
// ── COMO SE GUARDA, Y POR QUE ASI ──────────────────────────────────────────
//
// El campo de siempre ("Ingreso anual" del titular, el del cónyuge y el de
// cada dependiente) SIGUE SIENDO EL INGRESO PRINCIPAL, con su empleador y su
// tipo de empleo. Los ingresos de más van en una lista aparte, por persona:
//
//   [{ empleador: 'Uber', tipo: 'contractor_1099', monto: '$12,000' }, …]
//
// Titular y cónyuge la guardan en la columna `otrosIngresos` de la ficha
// (`{ titular: [...], conyuge: [...] }`); cada dependiente, dentro de su propio
// objeto en `dependents`. Se eligio NO cambiar lo que significa el campo de
// siempre porque unas cuarenta pantallas lo leen (reportes, planillas,
// importadores, correo masivo): ninguna se rompe ni cambia de numero por esto.
// Lo que SI suma los otros ingresos es todo lo que calcula un TOTAL —el de la
// persona, el del hogar y el que llena el cotizador del Mercado—, que es donde
// un ingreso de menos cambia el subsidio.
//
// Sin dependencias (salvo dineroDeTexto.js, que tampoco tiene), para que se
// pueda probar fuera de un navegador. La misma suma vive en el servidor, en
// `deskte-api/src/utils/ingresoDelHogar.ts`, como copia deliberada.

import { dolaresDeTexto } from './dineroDeTexto.js';

/** Tipos de ingreso que se ofrecen para un ingreso de más. */
export const TIPOS_DE_INGRESO = [
  { id: 'w2',              es: 'W2 (Empleado)',               en: 'W2 (Employee)' },
  { id: 'self_employed',   es: 'Cuenta propia',               en: 'Self-employed' },
  { id: 'contractor_1099', es: '1099 / Contratista',          en: '1099 / Contractor' },
  { id: 'retired',         es: 'Pensión / retiro',            en: 'Pension / retirement' },
  { id: 'social_security', es: 'Seguro Social',               en: 'Social Security' },
  { id: 'other',           es: 'Otro',                        en: 'Other' },
];

export function tipoDeIngresoLegible(id, lang) {
  const t = TIPOS_DE_INGRESO.find((x) => x.id === id);
  return t ? (lang === 'es' ? t.es : t.en) : (id || '');
}

/** Tope de filas por persona: ninguna persona tiene diez trabajos. */
export const MAX_OTROS_INGRESOS = 10;

const texto = (v, max) => String(v ?? '').trim().slice(0, max);

/**
 * La lista tal cual se guarda: solo las tres llaves, recortadas, sin filas
 * vacías. Una fila a medio llenar (solo el empleador, o solo el monto) se
 * conserva: es lo que la persona escribió.
 */
export function limpiarOtrosIngresos(lista) {
  if (!Array.isArray(lista)) return [];
  return lista
    .filter((f) => f && typeof f === 'object')
    .map((f) => ({ empleador: texto(f.empleador, 120), tipo: texto(f.tipo, 40), monto: texto(f.monto, 40) }))
    .filter((f) => f.empleador || f.monto)
    .slice(0, MAX_OTROS_INGRESOS);
}

/** Suma de los ingresos de más, en número (0 si no hay). */
export function sumaOtrosIngresos(lista) {
  if (!Array.isArray(lista)) return 0;
  return lista.reduce((s, f) => s + (dolaresDeTexto(f && f.monto) ?? 0), 0);
}

/**
 * Lo que gana la persona en total: el ingreso principal más los de más.
 * `null` si no hay ni una cifra — "sin dato" no es "$0" (un 0 es un ingreso
 * real y dispara el aviso de Medicaid).
 */
export function ingresoTotalDePersona(principal, lista) {
  const p = dolaresDeTexto(principal);
  const otros = Array.isArray(lista) ? lista.map((f) => dolaresDeTexto(f && f.monto)).filter((n) => n !== null) : [];
  if (p === null && otros.length === 0) return null;
  return (p ?? 0) + otros.reduce((a, b) => a + b, 0);
}

/** `$53,000`, como se lee en la casa (en-US). Vacío si no hay cifra. */
export function dineroLegible(n) {
  if (n === null || n === undefined || !Number.isFinite(n)) return '';
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}
